import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { useMeasure } from "react-use";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { useSeriesArticleContext } from "scmp-app/components/article/article-render/series-article/contexts";
import type { pagerSeriesArticleArticle$key } from "scmp-app/queries/__generated__/pagerSeriesArticleArticle.graphql";

import {
  Bookmark,
  Container,
  Headline,
  Left,
  List,
  NumberBlock,
  Right,
  StyledContentBookmark,
  StyledContentShareWidget,
  Tag,
  Title,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  isSticky?: boolean;
  reference: pagerSeriesArticleArticle$key;
};

export const SeriesArticlePager: FunctionComponent<Props> = ({
  className,
  isSticky = false,
  reference,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const [measureReference, { height }] = useMeasure<HTMLDivElement>();
  const { handleScrollTo, scrollState } = useSeriesArticleContext();

  const onNumberClick = (entityId: string) => {
    handleScrollTo?.(entityId, {
      duration: 1000,
      offset: -(appBarStickyHeight + height),
      smooth: true, // Scrolls to element + 50 pixels down the page
    });
  };

  const getIsHighlighted = (entityId: string) => {
    if (!entityId) return false;
    if (scrollState?.highlightedEntityId) return scrollState.highlightedEntityId === entityId;
    return scrollState?.toScrollEntityId === entityId;
  };

  const article = useFragment(
    graphql`
      fragment pagerSeriesArticleArticle on Article {
        headline
        entityId
        series {
          ... on Article {
            entityId
          }
          ... on Video {
            entityId
          }
          ... on Podcast {
            entityId
          }
        }
        ...contentShareWidgetContent
      }
    `,
    reference,
  );

  const getSimplifiedNumbers = () => {
    const blocks =
      article.series?.map((series, index) => ({
        entityId: series?.entityId ?? "",
        isActive: getIsHighlighted(series?.entityId ?? ""),
        number: index + 1,
      })) ?? [];
    const blockSize = blocks?.length ?? 0;

    if (blockSize > 6) {
      const currentIndex = blocks.findIndex(block => block.isActive) ?? 0;

      if (currentIndex < 4) {
        return [...blocks.slice(0, 4), null, blocks[blockSize - 1]];
      }

      if (currentIndex > 3 && currentIndex + 2 < blockSize) {
        return [
          ...blocks.slice(0, 1),
          null,
          ...blocks.slice(currentIndex - 2, currentIndex + 1),
          null,
          blocks[blockSize - 1],
        ];
      }

      if (currentIndex + 3 > blockSize) {
        return [...blocks.slice(0, 1), null, ...blocks.slice(blockSize - 4, blockSize)];
      }
    }
    return blocks;
  };

  const renderNumberBlocksList = () => {
    const blocks = getSimplifiedNumbers();

    return (
      <List>
        {blocks.map((block, index) => {
          if (block === null) {
            return <NumberBlock key={index}>...</NumberBlock>;
          }

          return (
            <NumberBlock
              $isActive={block?.isActive}
              key={index}
              onClick={() => {
                onNumberClick(block?.entityId ?? "");
              }}
            >
              {block?.number ?? ""}
            </NumberBlock>
          );
        })}
      </List>
    );
  };

  return (
    <Container className={className} ref={measureReference}>
      <Wrapper>
        <Left>
          <Title $isSticky={isSticky}>
            <Tag>SCMP Series:</Tag>
            <Headline>{article.headline}</Headline>
          </Title>

          {renderNumberBlocksList()}
        </Left>

        <Right $isSticky={isSticky}>
          <StyledContentShareWidget
            extraProps={{
              copyLink: {
                disablePortal: false,
                tooltipPlacement: "bottom",
                variant: "tooltip",
              },
            }}
            reference={article}
            style={{
              backgroundColor: "transparent",
            }}
            types={["facebook", "twitter", "copy-link", "email"]}
          />

          <Bookmark>
            <StyledContentBookmark
              entityId={article.entityId}
              key="content-bookmark"
              location="side"
            />
          </Bookmark>
        </Right>
      </Wrapper>
    </Container>
  );
};

SeriesArticlePager.displayName = "SeriesArticlePager";
